/** @jsx jsx */

import { useState, Fragment } from 'react';

import { Spacer } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import SuggestionsProvider from 'app/common/components/AutoCompleteTextInput/SuggestionsContext';
import HelpBox from 'app/developer-v3/components/HelpBox';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import RequestFields from 'app/developer-v3/components/RequestFields';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import { convertKvpsToObj } from 'app/developer-v3/utils';
import {
  useCurrentVersionReadOnlyReason,
  useRouter,
} from 'app/developer-v3/hooks';

import type { KeyValue } from 'app/developer-v3/components/KeyValueEditor';
import type { RequestSchema } from 'app/developer-v3/platformSchema/request';
import { getAppVersionUrl } from 'app/developer-v3/utils/url/url';

type Props = {
  getSuggestions: (operation: string) => Array<string>;
  initialRequestTemplate?: RequestSchema;
  isSaving: boolean;
  onSave: (requestTemplate: RequestSchema) => Promise<void>;
};

const defaultProps = {
  initialRequestTemplate: {
    removeMissingValuesFrom: {},
  },
};

function RequestTemplate({
  getSuggestions,
  initialRequestTemplate = defaultProps.initialRequestTemplate,
  isSaving,
  onSave,
}: Props) {
  const [requestTemplate, setRequestTemplate] = useState(
    initialRequestTemplate
  );

  const isReadOnly = !!useCurrentVersionReadOnlyReason();

  const onChangeParams = (paramsNext: Array<KeyValue>) => {
    setRequestTemplate({
      ...requestTemplate,
      params: convertKvpsToObj(paramsNext),
    });
  };

  const onChangeHeaders = (headersNext: Array<KeyValue>) => {
    setRequestTemplate({
      ...requestTemplate,
      headers: convertKvpsToObj(headersNext),
    });
  };

  const onChangeBody = (bodyNext: Array<KeyValue>) => {
    setRequestTemplate({
      ...requestTemplate,
      body: convertKvpsToObj(bodyNext),
    });
  };

  const onChangeOptions = (optionKey: string, optionValue: boolean) => {
    const { removeMissingValuesFrom = {} } = requestTemplate;
    const removeMissingValuesFromNext = {
      ...removeMissingValuesFrom,
      [optionKey]: optionValue,
    };
    setRequestTemplate({
      ...requestTemplate,
      removeMissingValuesFrom: removeMissingValuesFromNext,
    });
  };

  const router = useRouter();
  const { id: integrationId, version } = router.params;

  return (
    <SuggestionsProvider getSuggestions={getSuggestions}>
      <ManagerLayout shouldLimitWidth={true} title="Request Template">
        <HelpBox
          description={
            <Fragment>
              The fields you define here will be automatically injected to all
              trigger and action requests at the time of invocation.
              Authentication fields from the{' '}
              <a
                href={`${getAppVersionUrl(
                  integrationId,
                  version
                )}/authentication/edit`}
              >
                authentication definition
              </a>{' '}
              are automatically added here. You can add or remove fields as
              required, and also override them in the trigger or action
              configuration.
            </Fragment>
          }
          heading="Set Request Template"
        />
        <Spacer height={30} />
        <RequestFields
          onChangeBody={onChangeBody}
          onChangeHeaders={onChangeHeaders}
          onChangeOptions={onChangeOptions}
          onChangeParams={onChangeParams}
          removeMissingValuesFrom={requestTemplate.removeMissingValuesFrom}
        />
        <SaveButtonGroup
          isDisabled={isReadOnly}
          isSaving={isSaving}
          onSave={() => onSave(requestTemplate)}
          saveText="Save"
        />
      </ManagerLayout>
    </SuggestionsProvider>
  );
}

RequestTemplate.defaultProps = defaultProps;

export default RequestTemplate;
